import {
  BackLink,
  Color,
  colors,
  EnergyPulse,
  gradients,
  RADIUS,
  READING_MEASURE,
  SUPPORT_EMAIL,
  textVariants,
  useResolvedScheme,
  withAlpha,
  WithHalfdot,
  Wordmark,
} from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import GlobalStyles from "@mui/material/GlobalStyles";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  stepNumber,
  WELCOME_STEPS,
} from "~/components/onboarding/welcome-steps";
import { WelcomeVideo } from "~/components/onboarding/WelcomeVideo";
import { useProfileSave } from "~/components/settings/use-profile-save";
import { forceLightStatusBarGlyphs, syncStatusBarStyle } from "~/lib/capacitor";
import { markWelcomePassed } from "~/lib/onboarding";
import { useAuthStore } from "~/store/auth-store";

/** A landscape upload, so the player keeps its default 16:9 box. */
const WELCOME_VIDEO_ID = "ilVn02iRKhA";

/** Shared inline gutters — the band, the steps and the dock line up at every width. */
const GUTTERS = { xs: 2.5, md: 5 } as const;

/** The widest the page's content block gets, so 1440px is a composition, not a stretch. */
const CONTENT_WIDTH = 1160;

/**
 * Below this width the title steps down a rung of the scale.
 *
 * "PROTOCOL" is one unbreakable word, and Syncopate sets it 310px wide at He1's
 * 2.5rem. A 320pt phone offers 280px of column, and the band clips rather than
 * scrolls — so on the narrowest devices the headline would simply lose its last
 * letters. Every phone from 375pt up keeps the hero size.
 */
const NARROW_PHONE = "@media (max-width: 359.98px)";

/**
 * The band is dark in both colour schemes, so its text cannot use the semantic tiers —
 * `text.primary` would flip to near-black in light mode. These are the two rungs of the
 * dark-surface ladder the rest of the app already uses, measured against the band's
 * lightest stop (Violet900): 7.41:1 and 5.04:1. Silver700, the next rung down, reaches
 * only 4.08:1 there and is deliberately absent.
 */
const ON_BAND = colors[Color.Silver100];
const ON_BAND_MUTED = colors[Color.Silver500];

/**
 * Welcome to Protocol 30.5 — the first screen a member sees after the gate opens.
 *
 * Shell-less, like `PaywallRoute`: no tab bar, no side rail, and on a first run the
 * call to action is the only way on. That is the point of making it a page rather than
 * a card on the dashboard — arriving somewhere should feel like arriving.
 *
 * The layout is one dark arrival band (greeting and film, the same cinematic register
 * as the sign-in screen the member has just come through) over the ordinary content
 * surface that carries the six numbered chapters. The band is two columns on desktop
 * and stacked on a phone, decided in CSS; nothing here branches on a media query.
 *
 * The page scrolls as a document rather than inside a `100dvh` box. The copy is long,
 * and an inner scroller is not reachable from the keyboard until something inside it
 * has focus, loses iOS scroll restoration, and fights the collapsing browser bar. The
 * call to action is `position: sticky` instead, which keeps it on screen throughout and
 * still lands in its natural place at the end.
 *
 * Revisiting from the profile menu turns the call to action into a way back and adds a
 * back link at the top — the copy is worth re-reading, the acknowledgement is not worth
 * re-taking.
 */
export function WelcomeRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const { save } = useProfileSave();

  // Captured at render: `save` flips the store field, and the handler below must keep
  // behaving like the screen the member actually pressed.
  const firstRun = user !== null && !user.onboarding_completed;

  const scheme = useResolvedScheme();

  // Only the first run puts the band under the notch; a revisit has the back row, on
  // the ordinary paper surface, above it.
  useEffect(() => {
    if (!firstRun) return;
    void forceLightStatusBarGlyphs();
    return () => void syncStatusBarStyle(scheme);
  }, [firstRun, scheme]);

  const enter = () => {
    if (firstRun && user) {
      // Deliberately not awaited. `useProfileSave` swallows the failure, and
      // `markWelcomePassed` is what stops a dead connection from trapping the member
      // here — the page simply returns on the next app start, which is what the
      // account actually says. Waiting would hand a brand-new member on cellular a
      // dead button for the length of a fetch timeout. See `lib/onboarding.ts`.
      void save({ onboarding_completed: true });
      markWelcomePassed(user.id);
      navigate("/news", { replace: true });
      return;
    }
    navigate("/profile");
  };

  return (
    <Box
      data-testid="welcome.page"
      sx={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.paper",
      }}
    >
      {/* The band is the top of the document on a first run, so an iOS rubber-band
          pull reveals the page gradient — a pale strip flashing above a dark band,
          under the notch. Only while this screen owns the page. */}
      {firstRun ? (
        <GlobalStyles styles={{ html: { overscrollBehaviorY: "none" } }} />
      ) : null}

      {firstRun ? null : (
        <Box
          sx={{
            paddingInline: GUTTERS,
            paddingTop: "calc(12px + env(safe-area-inset-top))",
            paddingBottom: 1,
          }}
        >
          <Box sx={{ maxWidth: CONTENT_WIDTH, marginInline: "auto" }}>
            <BackLink
              to="/profile"
              label={t("common.backToProfile")}
              testId="welcome.back"
            />
          </Box>
        </Box>
      )}

      <Box
        component="header"
        sx={{
          position: "relative",
          // Keeps the band's own layering (bloom behind, content in front) to itself.
          // Without it those z-indexes join the root stacking context and the content
          // paints over the sticky call to action wherever the two overlap — which is
          // every phone short enough for the band to reach the bottom edge.
          isolation: "isolate",
          overflow: "hidden",
          backgroundImage: gradients.evening,
          paddingInline: GUTTERS,
          // On a first run there is no back row above, so the band runs under the
          // notch itself — that full bleed is the arrival.
          paddingTop: firstRun
            ? { xs: "calc(24px + env(safe-area-inset-top))", md: 8 }
            : { xs: 2, md: 6 },
          paddingBottom: { xs: 4, md: 8 },
        }}
      >
        {/* Kept off the text: the bloom's core is Silver100 at 95%, and laying it
            under the wordmark or the headline would lift the band's local luminance
            out of the ratios measured above. It blooms from the outer edge instead —
            behind the video on desktop, above the shoulder of the title on a phone. */}
        <EnergyPulse
          opacity={0.24}
          blur={48}
          // `size` is a single value; the responsive pair goes through `sx`, which the
          // component appends after its own base styles.
          sx={{
            position: "absolute",
            width: { xs: 260, md: 520 },
            height: { xs: 260, md: 520 },
            top: { xs: -150, md: -240 },
            right: { xs: -110, md: -200 },
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            maxWidth: CONTENT_WIDTH,
            marginInline: "auto",
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              md: "minmax(0, 1fr) minmax(0, 1.05fr)",
            },
            alignItems: "center",
            columnGap: 6,
            rowGap: { xs: 3.5, md: 0 },
          }}
        >
          <Box>
            <Wordmark
              size={24}
              color={Color.Silver100}
              // The lockup's dots in brand violet reach only 2.55:1 on this band. The
              // dark-mode violet text tint is the step that exists for exactly this.
              markColor={Color.VioletTextDark}
            />

            <Typography
              variant="He1"
              component="h1"
              sx={{
                color: ON_BAND,
                marginTop: { xs: 2, md: 3 },
                [NARROW_PHONE]: { fontSize: textVariants.H1.fontSize },
              }}
            >
              <WithHalfdot size={14} color={Color.VioletTextDark}>
                {t("welcome.title")}
              </WithHalfdot>
            </Typography>

            <Typography
              variant="H3"
              data-testid="welcome.lead"
              sx={{
                color: ON_BAND,
                marginTop: { xs: 1.5, md: 2 },
                maxWidth: READING_MEASURE,
              }}
            >
              {t("welcome.lead")}
            </Typography>

            <Typography
              variant="T1"
              sx={{
                color: ON_BAND_MUTED,
                marginTop: 1.25,
                maxWidth: READING_MEASURE,
              }}
            >
              {t("welcome.body")}
            </Typography>

            <Box
              sx={{
                display: "inline-flex",
                marginTop: { xs: 2, md: 2.5 },
                paddingBlock: 0.75,
                paddingInline: 1.75,
                borderRadius: `${RADIUS.full}px`,
                border: "1px solid",
                borderColor: withAlpha(ON_BAND, 0.28),
                backgroundColor: withAlpha(ON_BAND, 0.08),
              }}
            >
              <Typography variant="T3" sx={{ color: ON_BAND }}>
                {t("welcome.exclusive")}
              </Typography>
            </Box>
          </Box>

          <WelcomeVideo
            videoId={WELCOME_VIDEO_ID}
            title={t("welcome.videoTitle")}
            playLabel={t("welcome.videoPlay")}
          />
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          paddingInline: GUTTERS,
          paddingTop: { xs: 4, md: 8 },
          paddingBottom: { xs: 3, md: 6 },
        }}
      >
        <Box sx={{ maxWidth: CONTENT_WIDTH, marginInline: "auto" }}>
          <Typography
            variant="H1"
            component="h2"
            sx={{ color: "text.primary" }}
          >
            {t("welcome.guideHeading")}
          </Typography>

          <Box
            component="ol"
            // WebKit drops the list role from a list whose markers are removed, and
            // WebKit is both the e2e engine and the engine the iOS shell ships. The
            // explicit roles put the "list of 6" announcement back.
            role="list"
            sx={{
              listStyle: "none",
              margin: 0,
              padding: 0,
              marginTop: { xs: 2.5, md: 4 },
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "repeat(2, minmax(0, 1fr))",
              },
              gap: { xs: 2, md: 3 },
              alignItems: "stretch",
            }}
          >
            {WELCOME_STEPS.map((step, index) => (
              <StepCard key={step.key} index={index} step={step} />
            ))}
          </Box>

          <Typography
            variant="H3"
            data-testid="welcome.closing"
            sx={{
              color: "text.secondary",
              marginTop: { xs: 4, md: 6 },
              maxWidth: READING_MEASURE,
            }}
          >
            {t("welcome.closing")}
          </Typography>
        </Box>
      </Box>

      {/* Sticky rather than fixed, and the last flex child, so it rides the bottom
          edge while the page scrolls and then simply ends the page. `marginTop: auto`
          keeps it at the bottom on a short page — a revisit on a tall desktop window. */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          marginTop: "auto",
          backgroundColor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
          paddingInline: GUTTERS,
          paddingTop: 1.5,
          paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
        }}
      >
        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={enter}
          data-testid="welcome.enter"
          sx={{ maxWidth: 560, marginInline: "auto", display: "flex" }}
        >
          {t(firstRun ? "welcome.enter" : "common.backToProfile")}
        </Button>
      </Box>
    </Box>
  );
}

function StepCard({
  index,
  step,
}: {
  index: number;
  step: (typeof WELCOME_STEPS)[number];
}) {
  const { t } = useTranslation();
  const Icon = step.icon;

  return (
    <Paper
      component="li"
      role="listitem"
      variant="outlined"
      data-testid={`welcome.step-${step.key}`}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        paddingBlock: { xs: 2.5, md: 3 },
        paddingInline: { xs: 2.5, md: 3 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Icon fontSize="small" sx={{ color: "link.main", flexShrink: 0 }} />
        <Typography
          variant="OV1"
          sx={{
            color: "link.main",
            flexShrink: 0,
            // 01-06 read as a column, not six unrelated numbers.
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {stepNumber(index)}
        </Typography>
        <Typography
          variant="OV1"
          component="h3"
          sx={{ color: "text.primary", minWidth: 0 }}
        >
          {t(`welcome.steps.${step.key}.label`)}
        </Typography>
      </Box>

      <Typography
        variant="T2"
        sx={{
          color: "text.secondary",
          marginTop: 1.5,
          maxWidth: READING_MEASURE,
        }}
      >
        {t(`welcome.steps.${step.key}.body`)}
        {step.showsSupportEmail ? (
          <>
            {" "}
            {/* The one link on the page that leaves the app, and the one case
                `lib/external-link.ts` cannot serve: the in-app browser it opens
                (SFSafariViewController / Custom Tabs) has no mailto scheme. Both the
                WebView and a desktop browser hand a bare mailto to the mail client. */}
            <Link
              href={`mailto:${SUPPORT_EMAIL}`}
              data-testid="welcome.support-email"
              sx={{ color: "link.main" }}
            >
              {SUPPORT_EMAIL}
            </Link>
            .
          </>
        ) : null}
      </Typography>
    </Paper>
  );
}
